/**
 * Бесплатный аудит — вход в воронку (спека §4).
 *
 * Этого экрана не было, а лендинг на него уже вёл: единственная конверсионная
 * форма витрины отправляла `GET ${APP}/audit?domain=…` с подписью «Бесплатно ·
 * Без регистрации · Результат за 2 минуты», маршрут `*` уводил гостя на форму
 * входа, домен терялся, а завести учётную запись было нечем. Обещание под
 * кнопкой было неправдой, и весь вход в воронку не работал.
 *
 * Маршрут **публичный**, как `/invite/:token`: аудит на то и бесплатный, что до
 * него не нужна сессия. Отсюда и порядок — сначала результат, потом
 * предложение его забрать, а не наоборот.
 *
 * Конвейер по контракту:
 *
 * 1. `POST /public/audits` → `202` с `Location` и телом `Audit` (или `200`,
 *    если такой аудит уже собран);
 * 2. `GET /public/audits/{slug}` по `Retry-After`, пока состояние `queued` или
 *    `running`;
 * 3. `ready` — показываем балл и статус, дальше присвоение через регистрацию.
 *
 * Публичная ссылка живёт семь дней, после чего состояние становится `expired` —
 * это не ошибка, и говорится об этом словами, а не кодом.
 *
 * **Проверки «вы не робот» здесь нет.** Она стояла ровно на входе в воронку и
 * ломалась в браузере на загрузке чужой статики внутри своего iframe: человек
 * читал «Проверка "вы не робот" сломалась в браузере» вместо аудита. Виджет,
 * его состояние, повторы и разбор его отказов удалены целиком вместе с
 * `api/captcha.ts`. Злоупотребление ловится там, где оно стоит денег, — на
 * сервере: один аудит на домен в московские сутки, суточный потолок запусков с
 * одного адреса и скрытое поле-ловушка.
 *
 * **С лендинга аудит начинает считаться сразу.** Форма витрины отправляет
 * `GET /audit?domain=…`, и до этой правки человек, уже набравший домен, попадал
 * на ту же форму и набирал то же самое второй раз. Теперь домен из адреса —
 * это готовый запуск: экран сразу показывает «Считаем». Прямой заход без
 * параметров — обычная форма с одним полем.
 *
 * **Согласие берётся галочкой, и она обязательна.** Пока аудит запускался по
 * одному домену, согласия не требовалось: персональных данных в домене нет. С
 * 11 августа 2026 запуск идёт по рабочей почте, и с ней появляется всё
 * остальное. Пассивная строка «нажимая кнопку, вы даёте согласие» здесь не
 * годится по той же причине, по которой она не годится на оплате
 * (`routes/Checkout.tsx`): ч. 1 ст. 9 152-ФЗ требует согласия конкретного,
 * предметного, информированного и однозначного, а отправка формы однозначности
 * не даёт. Галочка не проставлена заранее и не блокирует кнопку — она блокирует
 * отправку и объясняет, чего не хватает.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Mark } from '../ui/Mark';
import { Field } from '../ui/form';
import { ErrorNotice, Pipeline, Skeleton } from '../ui/state';
import { scanSteps } from '../ui/screen-states';
import { requestWith } from '../api/client';
import { useSessionState } from '../session';
import { ApiError } from '../api/problem';
import { countOf, coverageOf, score as scoreFmt, statusLadder } from '@shared/src/format';
import { isEmailShaped, isWorkEmail } from '@shared/src/domain';
import { LEGAL, LEGAL_LINK_PROPS } from '../legal';
import { rememberClaim } from '../pending-claim';
import type { components } from '../api/schema';
import { AUDIT, PUBLIC_REPORT_LIFETIME } from '@shared/src/product';

type Audit = components['schemas']['Audit'];

/**
 * Слаг последнего аудита этой вкладки.
 *
 * Нужен, чтобы перезагрузка страницы не теряла результат: аудит идёт около двух
 * минут, и человек, обновивший вкладку, иначе запускал бы его заново — а квота
 * на бесплатные аудиты общая и конечная.
 *
 * `sessionStorage`, а не `localStorage`: это состояние одной вкладки, а не
 * данные, которые должны пережить закрытие браузера.
 */
export const AUDIT_SLUG_KEY = 'seeno.audit.slug';

/**
 * Токен браузерной сессии, из которой аудит запущен.
 *
 * Приходит **заголовком** `X-Seeno-Audit-Session` в ответе на запуск и больше
 * не показывается нигде. Без него `session_token` в `POST /audits/claim` взять
 * негде, а он и решает, кому принадлежит право присвоить аудит: тому, кто его
 * запускал, а не тому, кому переслали публичную ссылку.
 *
 * Заголовок, а не кука, потому что запуск идёт с маркетингового сайта — другого
 * источника, куда кука кабинета не ставится.
 *
 * `sessionStorage`: доступа к данным аккаунта токен не даёт, но это всё же
 * доказательство происхождения, и переживать закрытие браузера ему незачем.
 */
export const AUDIT_SESSION_KEY = 'seeno.audit.session';

/**
 * Что именно набрал человек для аудита, лежащего в `AUDIT_SLUG_KEY`.
 *
 * Без этой записи слаг во вкладке ПЕРЕБИВАЛ переход с лендинга, и получалась
 * ровно та жалоба, ради которой всё это делается, только наизнанку: человек
 * проверил сайт А, вернулся на витрину В ТОЙ ЖЕ ВКЛАДКЕ, ввёл сайт Б — и снова
 * видел отчёт по А, а Б не уходил на сервер вовсе.
 *
 * Хранится СТРОКА, КАК ЕЁ НАБРАЛИ, а не разобранный домен: сравнение идёт с
 * такой же строкой из адреса, а нормализация живёт на сервере. Сравнивать
 * набранное с нормализованным `website_domain` значило бы переносить сюда
 * punycode, отсечение `www.` и разбор адреса — то есть повторять сервер и
 * расходиться с ним.
 */
export const AUDIT_REQUEST_KEY = 'seeno.audit.request';

/** Интервал опроса по умолчанию, если сервер не сказал иначе. */
const POLL_FALLBACK = 3;

/** Сравнение набранного: регистр и лишние пробелы значения не имеют. */
function sameRequest(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * Домен так, как его набрал человек.
 *
 * В базе домен хранится в ASCII, потому что таково ограничение колонки, и
 * `пик.рф` лежит там как `xn--h1aen.xn--p1ai`. На экране это читалось «Разбираем
 * xn--h1aen» — набор, в котором человек не узнаёт ни свой сайт, ни своё
 * название. Раскодированную запись отдаёт API полем `website_display`; запасной
 * путь на `website_domain` оставлен на время выката, пока в кэше CDN может
 * лежать ответ без нового поля.
 */
function shown(audit: Audit): string {
  return audit.website_display || audit.website_domain;
}

/**
 * Публичный адрес отчёта — тот же, что уходит в письмо.
 *
 * Источник один и он серверный: `report_url` строится в API (`{APP}/audit/{slug}`),
 * и собирать его второй раз на экране значило бы завести вторую правду. Ровно
 * так расходятся адрес на экране и адрес в письме.
 *
 * Запасной путь нужен на время выката: в кэше CDN может лежать ответ старой
 * сборки, где поля ещё нет, а сказать «сохраните ссылку» и не показать её —
 * то же самое обещание без ссылки, ради снятия которого всё это и написано.
 */
function publicLink(audit: Audit): string {
  // Слаг кодируется и здесь. Сегодня он заведомо буквенно-цифровой, но правило
  // «адрес собирается из значения» не должно держаться на этом: две ссылки на
  // один отчёт, собранные по-разному, однажды разойдутся именно на первом слаге,
  // который перестанет быть безопасным.
  return (
    audit.report_url ||
    `${window.location.origin}/audit/${encodeURIComponent(audit.slug)}`
  );
}

// Состояния, после которых опрашивать больше нечего. Перечислены КОНЕЧНЫЕ, а не
// промежуточные, и это не стилистика: список промежуточных растёт со стороны
// сервера, а список конечных — нет. Пока здесь стояло «опрашиваем, пока queued
// или running», добавление фазы `preparing` — разбора сайта, ради которого экран
// и рисует первый шаг, — молча увело бы КАЖДЫЙ новый аудит в ветку «готово»:
// балла там ещё нет, и человек на первой же секунде читал бы «Не смогли
// посчитать» вместо ожидания.
const TERMINAL_STATES: ReadonlyArray<Audit['state']> = ['ready', 'failed', 'expired'];

/**
 * Имя скрытого поля-ловушки. Одно и то же на лендинге, здесь и в контракте.
 *
 * Ловушка стоит вместо снятой капчи и берёт тот же класс, который капча ловила
 * на самом деле: обходчика, который заполняет форму по именам полей. Человеку
 * она не стоит ни клика — поле скрыто и вынесено из порядка обхода, — а решение
 * принимает сервер: клиентская проверка сама по себе не защищает ничего, робот
 * ходит прямо в API.
 *
 * Имя НАРОЧНО не из словаря профиля. Сначала поле звалось `company` — и это
 * была ловушка на живого человека: `company` и `organization` подставляет
 * автозаполнение браузера и любой менеджер паролей, ничего не спрашивая, а
 * `autocomplete="off"` они соблюдать не обязаны. Посетитель получил бы отказ за
 * то, что у него в профиле записано название его компании, — и, поскольку форма
 * витрины уходит методом GET, это название ещё и уехало бы в адресную строку и
 * в журналы. Обходчику имя безразлично: он заполняет всё, что нашёл.
 */
const TRAP_FIELD = 'audit_note';

type Load =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'running'; audit: Audit }
  | { kind: 'ready'; audit: Audit }
  | { kind: 'failed'; error: unknown };

export function Audit() {
  const [params] = useSearchParams();
  // Маршрут публичный, но вошедший человек по нему тоже ходит — и ему нужен
  // другой выход: не регистрация, а присвоение отчёта своей организации.
  const { state } = useSessionState();
  const signedIn = state.kind === 'signed';
  const [website, setWebsite] = useState(params.get('domain') ?? '');
  // Почта обязательна с 11 августа 2026 (решение фаундера): отчёт уходит на неё
  // письмом, и адрес остаётся у нас для связи. С витрины она НЕ передаётся: та
  // форма уходит методом GET, и адрес человека лёг бы в строку браузера, в его
  // историю, в журналы CDN и в заголовок перехода. Поэтому домен приезжает
  // параметром, а почта спрашивается здесь.
  const [email, setEmail] = useState('');
  // Показывать ли отказ по почте. До первой попытки отправки экран молчит:
  // красное под полем, которое человек ещё не дозаполнил, — это претензия к
  // тому, чего он не делал.
  const [emailTouched, setEmailTouched] = useState(false);
  /**
   * Согласие на обработку персональных данных.
   *
   * Здесь стояла пассивная строка «нажимая кнопку, вы даёте согласие», и это
   * был двойной стандарт внутри одного продукта: экран оплаты уже говорит, что
   * «пассивная строка — не согласие», и гейтит отправку активной галочкой.
   * К персональным данным закон требует ровно того же: согласие конкретное,
   * предметное, информированное и однозначное (ч. 1 ст. 9 152-ФЗ), а отправка
   * формы однозначности не даёт. Галочка не проставлена заранее — иначе она
   * не отличается от строки под кнопкой.
   */
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  // Согласие на письма о продукте — ОТДЕЛЬНОЕ и необязательное. Основания
  // разные: обработка данных — 152-ФЗ, рассылка — ч. 1 ст. 18 38-ФЗ, и одной
  // галочкой их не закрыть. Отчёт приходит независимо от неё: ставить
  // результат в зависимость от согласия на рассылку запрещает ч. 3 ст. 9
  // 152-ФЗ.
  const [marketing, setMarketing] = useState(false);
  // Значение ловушки живёт в состоянии, а не читается из DOM при отправке:
  // поле управляемое, как и остальные, и его нечем «забыть подключить».
  const [trap, setTrap] = useState(params.get(TRAP_FIELD) ?? '');
  // Пришёл ли человек с доменом в адресе. Раньше по этому признаку аудит
  // запускался сам: вводить было нечего. Теперь есть — почта, — и молча
  // стартовать нельзя, иначе запуск уйдёт без адреса, на который обещано
  // письмо. Домен из перехода остаётся подставленным в поле, человек
  // дозаполняет одну строку и нажимает кнопку.
  const [handoff] = useState(
    () =>
      (params.get('domain') ?? '').trim() !== '' &&
      (params.get(TRAP_FIELD) ?? '').trim() === '',
  );
  // Начальное состояние выводится из хранилища прямо здесь, а не выставляется
  // эффектом: аудит этой вкладки либо уже запущен, либо нет, и это известно до
  // первой отрисовки. Эффект остаётся только на то, для чего эффекты и нужны, —
  // подписку на внешний источник, то есть на опрос.
  const [load, setLoad] = useState<Load>(() =>
    sessionStorage.getItem(AUDIT_SLUG_KEY) ? { kind: 'starting' } : { kind: 'idle' },
  );
  const timer = useRef<number | undefined>(undefined);
  const abort = useRef<AbortController | null>(null);
  // Номер живого запуска: результат отменённого не имеет права рисовать экран.
  const run = useRef(0);
  // Номер запуска, чей ответ уже записан. Ответы могут прийти не в том порядке,
  // в каком уходили, и поздний ответ РАННЕГО запуска не имеет права затереть
  // слаг и токен позднего.
  const saved = useRef(0);

  // Опрос состояния. Живёт отдельно от запуска: к нему возвращаются и после
  // перезагрузки вкладки, где запуска не было вовсе.
  const poll = useCallback((slug: string) => {
    // **Всегда новый** контроллер, а не переиспользованный из `abort.current`.
    // Переиспользованный уже отменён: размонтирование вызывает `abort()`, и в
    // строгом режиме React монтирует эффект дважды — второй заход получал
    // отменённый контроллер, каждый запрос падал в `AbortError`, ветка
    // отмены молча выходила, и экран навсегда оставался на «Запускаем аудит».
    // То же самое случилось бы при любом повторном монтировании.
    const controller = new AbortController();
    abort.current?.abort();
    abort.current = controller;

    const tick = () => {
      requestWith<Audit>(`/public/audits/${encodeURIComponent(slug)}`, {
        signal: controller.signal,
      })
        .then(({ body, retryAfter }) => {
          if (!TERMINAL_STATES.includes(body.state)) {
            setLoad({ kind: 'running', audit: body });
            timer.current = window.setTimeout(tick, (retryAfter ?? POLL_FALLBACK) * 1000);
            return;
          }
          setLoad({ kind: 'ready', audit: body });
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setLoad({ kind: 'failed', error });
        });
    };

    tick();
  }, []);

  // Запуск аудита. Отдельно от обработчика формы, потому что вызывается ещё и
  // переходом с лендинга, где события формы нет вовсе.
  const launch = useCallback(
    async (target: string, mailbox: string, trapValue: string, letters: boolean) => {
      const mine = ++run.current;
      const mineStill = () => run.current === mine;
      setLoad({ kind: 'starting' });
      try {
        const { body, headers } = await requestWith<Audit>('/public/audits', {
          method: 'POST',
          // Ловушка уходит на сервер ВСЕГДА и всегда пустой у человека:
          // решение принимает сервер, потому что робот до формы может и не
          // дойти — он ходит прямо в API.
          body: {
            website: target.trim(),
            email: mailbox.trim().toLowerCase(),
            consent: true,
            marketing_consent: letters,
            [TRAP_FIELD]: trapValue,
          },
        });
        // Записывается ДО проверки «мой ли это запуск», и это принципиально.
        // Ответ уже пришёл — значит аудит на сервере СОЗДАН: бесплатный аудит
        // суток по этому домену израсходован. Токен происхождения отдаётся
        // **один раз** — здесь; не сохранив его, присвоить аудит будет уже нечем
        // (слаг публичен и правом не является). Уйти отсюда молча из-за того, что
        // человек в этот момент нажал «Вернуться к форме», значит сжечь ему сутки
        // и оставить аудит ничей навсегда.
        //
        // Порядок ответов не гарантирован, поэтому пишет только САМЫЙ ПОЗДНИЙ из
        // пришедших запусков: иначе догнавший ответ первого затёр бы слаг второго
        // своим, а токен — чужим, и присвоение ушло бы не к тому аудиту.
        if (mine > saved.current) {
          saved.current = mine;
          sessionStorage.setItem(AUDIT_SLUG_KEY, body.slug);
          // Рядом со слагом — то, что набрал человек: по нему следующий заход
          // отличит «досмотреть свой аудит» от «посчитать другой сайт».
          sessionStorage.setItem(AUDIT_REQUEST_KEY, target.trim());
          const session = headers.get('X-Seeno-Audit-Session');
          if (session) sessionStorage.setItem(AUDIT_SESSION_KEY, session);
        }
        if (!mineStill()) return;
        if (
          body.state === 'ready' ||
          body.state === 'failed' ||
          body.state === 'expired'
        ) {
          setLoad({ kind: 'ready', audit: body });
          return;
        }
        setLoad({ kind: 'running', audit: body });
        poll(body.slug);
      } catch (caught) {
        if (!mineStill()) return;
        setLoad({ kind: 'failed', error: caught });
      }
    },
    [poll],
  );

  // Восстановление после перезагрузки. Аудит этой вкладки досматривается, но
  // сам по переходу с витрины больше не начинается: без почты запускать нечего.
  useEffect(() => {
    const slug = sessionStorage.getItem(AUDIT_SLUG_KEY);
    const asked = params.get('domain') ?? '';
    // Слаг во вкладке досматривается только если он ПРО ТОТ ЖЕ САЙТ, что просят
    // сейчас. Иначе побеждает адрес: человек, вернувшийся на витрину в этой же
    // вкладке и набравший другой сайт, обязан получить аудит того сайта, а не
    // отчёт по прошлому. Пока слаг перебивал переход, второй домен не уходил на
    // сервер вовсе.
    const mine =
      slug &&
      (!handoff || sameRequest(sessionStorage.getItem(AUDIT_REQUEST_KEY) ?? '', asked));
    if (mine && slug) poll(slug);
    else if (handoff) {
      // Чужой аудит из этой вкладки больше не наш: его слаг и его токен
      // происхождения не имеют отношения к тому, что запускается сейчас, и
      // остаться они могут только чтобы всё перепутать.
      sessionStorage.removeItem(AUDIT_SLUG_KEY);
      sessionStorage.removeItem(AUDIT_SESSION_KEY);
      sessionStorage.removeItem(AUDIT_REQUEST_KEY);
    }
    return () => {
      abort.current?.abort();
      if (timer.current) window.clearTimeout(timer.current);
    };
    // Только при первой отрисовке: дальше состоянием управляют запуск и опрос.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function start(event: FormEvent) {
    event.preventDefault();
    setEmailTouched(true);
    setConsentError(!consent);
    if (!isWorkEmail(email) || !consent) return;
    void launch(website, email, trap, marketing);
  }

  function again() {
    // Номер запуска сдвигается, чтобы ответ уходящего запроса не дорисовал свою
    // ошибку поверх формы, к которой человек уже вернулся.
    run.current += 1;
    sessionStorage.removeItem(AUDIT_SLUG_KEY);
    sessionStorage.removeItem(AUDIT_REQUEST_KEY);
    abort.current?.abort();
    abort.current = null;
    if (timer.current) window.clearTimeout(timer.current);
    setLoad({ kind: 'idle' });
  }

  return (
    <main className="auth">
      <div className="auth-card audit-card">
        <a className="brand" href="https://seeno.ru/">
          <Mark />
          seeno
        </a>

        {load.kind === 'idle' || load.kind === 'failed' ? (
          <>
            <h1>Бесплатный аудит</h1>
            <p className="auth-lead">
              Спросим у нейросетей то, что спрашивают ваши покупатели, и покажем, назвали
              вас или конкурентов. Занимает {AUDIT.duration}.
            </p>

            {load.kind === 'failed' ? <AuditError error={load.error} /> : null}

            <form onSubmit={start} noValidate>
              <Field
                id="website"
                label="Адрес сайта"
                hint="Например, example.ru — можно с http:// и без"
              >
                {(props) => (
                  <input
                    {...props}
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    required
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                  />
                )}
              </Field>

              {/* Почта. Обязательна: отчёт уходит письмом, и адрес остаётся у
                  нас для связи. Только рабочая — решение фаундера; список
                  публичных служб лежит в общем слое рядом с разбором домена,
                  чтобы витрина и кабинет считали одинаково.

                  Клиентская проверка здесь — вежливость, а не защита: запуск
                  можно позвать прямо в API мимо этой формы, поэтому тот же
                  список обязан стоять на сервере. */}
              <Field
                id="email"
                label="Рабочая почта"
                hint="Сюда придёт отчёт. Почту на бесплатных ящиках не принимаем"
                error={
                  emailTouched && !isWorkEmail(email)
                    ? isEmailShaped(email)
                      ? 'Нужен адрес рабочей почты: на бесплатных ящиках отчёт не отправляем'
                      : 'Похоже, в адресе опечатка'
                    : undefined
                }
              >
                {(props) => (
                  <input
                    {...props}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    spellCheck={false}
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    onBlur={() => setEmailTouched(true)}
                  />
                )}
              </Field>

              {/* Ловушка. Не `type="hidden"`: скрытые поля обходчики как раз
                  пропускают, а видимое глазу браузера текстовое поле с
                  «интересным» именем заполняют охотно. От человека оно спрятано
                  всеми тремя способами сразу — уведено за край экрана, убрано из
                  порядка обхода клавиатурой и скрыто от программ чтения с
                  экрана, — поэтому заполнить его случайно нельзя. */}
              <input
                type="text"
                name={TRAP_FIELD}
                className="trap"
                value={trap}
                onChange={(event) => setTrap(event.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              {/* Галочка стоит ДО кнопки и гейтит отправку — так же, как
                  акцепт оферты на оплате. Кнопка при снятой галочке остаётся
                  живой: отключённая кнопка без объяснения читается как поломка,
                  а так человек видит, чего не хватает. */}
              <label className={`consent${consentError ? ' invalid' : ''}`}>
                <input
                  type="checkbox"
                  name="consent"
                  checked={consent}
                  aria-invalid={consentError ? true : undefined}
                  aria-describedby="consent-error"
                  onChange={(event) => {
                    setConsent(event.target.checked);
                    if (event.target.checked) setConsentError(false);
                  }}
                />
                <span>
                  Даю{' '}
                  <a href={LEGAL.consent} {...LEGAL_LINK_PROPS}>
                    согласие на обработку персональных данных
                  </a>{' '}
                  и подтверждаю, что прочитал{' '}
                  <a href={LEGAL.privacy} {...LEGAL_LINK_PROPS}>
                    политику конфиденциальности
                  </a>
                </span>
              </label>
              <span
                className={`hint${consentError ? ' error' : ''}`}
                id="consent-error"
                role={consentError ? 'alert' : undefined}
              >
                {consentError
                  ? 'Без согласия на обработку почты отчёт отправить некуда'
                  : 'Отчёт придёт на указанный адрес'}
              </span>

              {/* Вторая галочка снята по умолчанию и отправку не гейтит.
                  Проставленная заранее — это не согласие: закон требует
                  однозначного, а «человек не снял» однозначным не бывает. */}
              <label className="consent">
                <input
                  type="checkbox"
                  name="marketing_consent"
                  checked={marketing}
                  onChange={(event) => setMarketing(event.target.checked)}
                />
                <span>Хочу получать письма Seeno о видимости брендов в нейросетях</span>
              </label>
              <span className="hint">
                Необязательно. Отказаться можно ссылкой в любом письме
              </span>

              <button
                className="btn btn-primary"
                type="submit"
                disabled={!website.trim() || !email.trim()}
              >
                Запустить бесплатный аудит
              </button>
            </form>
          </>
        ) : null}

        {load.kind === 'starting' ? (
          <>
            {/* Тот же заголовок, что и у идущего аудита. Человек с лендинга
                попадает сюда сразу, и «Считаем» здесь — правда: запрос на
                запуск уже ушёл. Отдельный экран про капчу тут стоял ровно для
                того, чтобы предупредить о ней; капчи нет — и предупреждать не о
                чем. */}
            <h1>Считаем</h1>
            <p className="auth-lead">
              {website.trim() ? (
                <>
                  Разбираем <b>{website.trim()}</b>. Занимает {AUDIT.duration}.
                </>
              ) : (
                `Занимает ${AUDIT.duration}.`
              )}
            </p>
            <Skeleton rows={2} />
            <button className="lnk-btn" type="button" onClick={again}>
              Вернуться к форме
            </button>
          </>
        ) : null}

        {load.kind === 'running' ? (
          <>
            <h1>Считаем</h1>
            <p className="auth-lead">
              Разбираем <b>{shown(load.audit)}</b>. Страницу можно не держать открытой —
              результат сохранится за этой вкладкой.
            </p>
            <Pipeline
              steps={scanSteps()}
              current={
                load.audit.state === 'preparing' || load.audit.state === 'queued' ? 0 : 1
              }
            />
            {load.audit.tasks_total ? (
              <p className="stamp">
                Закрыто{' '}
                {coverageOf(
                  load.audit.tasks_done ?? 0,
                  load.audit.tasks_total,
                  'задача',
                  'задачи',
                  'задач',
                )}{' '}
                замера
              </p>
            ) : null}
          </>
        ) : null}

        {load.kind === 'ready' ? (
          <Result audit={load.audit} onAgain={again} signedIn={signedIn} />
        ) : null}
      </div>
    </main>
  );
}

/**
 * Результат.
 *
 * Балл идёт вместе со статусом словом и с основанием — по скольким вопросам и
 * скольким движкам он посчитан. Балл без основания в этом продукте не
 * показывается нигде.
 */
function Result({
  audit,
  onAgain,
  signedIn,
}: {
  audit: Audit;
  onAgain: () => void;
  /** Вошедшему предлагается присвоение, а не регистрация: аккаунт у него уже есть. */
  signedIn: boolean;
}) {
  if (audit.state === 'expired') {
    return (
      <>
        <h1>Ссылка истекла</h1>
        <p className="auth-lead">
          {PUBLIC_REPORT_LIFETIME} Этот срок уже прошёл. Аудит можно запустить заново —
          это бесплатно.
        </p>
        <button className="btn btn-primary" type="button" onClick={onAgain}>
          Запустить заново
        </button>
      </>
    );
  }

  if (audit.state === 'failed' || audit.score === null || audit.score === undefined) {
    return (
      <>
        <h1>Не смогли посчитать</h1>
        <p className="auth-lead">
          Аудит для <b>{shown(audit)}</b> не собрался — движки не ответили или сайт не
          удалось разобрать. Мы показываем это, а не выдуманный балл.
        </p>
        <button className="btn btn-primary" type="button" onClick={onAgain}>
          Попробовать ещё раз
        </button>
      </>
    );
  }

  return (
    <>
      <h1>{audit.brand_name}</h1>
      <p className="auth-lead">{shown(audit)}</p>

      <div className="audit-score">
        <div className="value tnum">{scoreFmt(audit.score)}</div>
        <div className="audit-status">{statusLadder(audit.score)}</div>
      </div>

      {/* Основание: балл без него — не показание. Числа склоняются через
          `countOf`: «4 движков» и «1 вопросов» — та же заморозка формы, из-за
          которой на главном экране висело «29 задач не удалась». */}
      <p className="stamp">
        Индекс ИИ-видимости ·{' '}
        {countOf(audit.questions_planned, 'вопрос', 'вопроса', 'вопросов')} ·{' '}
        {countOf(audit.engines?.length ?? 0, 'движок', 'движка', 'движков')} · публичная
        ссылка действует 7 дней
      </p>

      <p className="auth-lead">
        Это один замер. Дальше мы меряем то же самое каждую неделю, показывает, кого
        называют вместо вас, и что с этим делать.
      </p>

      {/* Главное действие экрана — ОТКРЫТЬ отчёт.
          Здесь показывался голый балл и кнопка «забрать», а сам отчёт —
          разрез по движкам, конкуренты, цитаты, готовность сайта — лежал
          готовым на `/audit/{slug}`, и ссылки туда не было ни одной. Человек
          видел число и уходил; единственным способом вернуться к своим числам
          оставалось запустить тот же домен заново — а это новая сетка и новые
          деньги, как только в Москве наступили новые сутки. */}
      <Link className="btn btn-primary" to={`/audit/${encodeURIComponent(audit.slug)}`}>
        Открыть полный отчёт
      </Link>

      {/* Вошедшего нельзя вести на регистрацию: маршрут `/auth/*` под сессией
          уводит на обзор, и кнопка «Забрать результат» не забирала ничего —
          человек с готовым отчётом и готовым аккаунтом попадал в кольцо.
          Гостю адрес нужен тот же: гостевая ветка маршрутов сама отправит его
          на вход, запомнив путь, и вернёт к присвоению. Регистрация без слага
          теряла отчёт молча. */}
      <Link
        className="btn btn-ghost"
        to={`/audits/claim?slug=${encodeURIComponent(audit.slug)}`}
        onClick={() => rememberClaim(audit.slug)}
      >
        {signedIn ? 'Забрать отчёт в организацию' : 'Забрать результат в кабинет'}
      </Link>

      {/* «По этой ссылке» — теперь действительно ссылка, а не обещание.
          Публичный адрес приходит от сервера (`report_url`): он же уходит в
          письмо, и расходиться экрану с письмом нельзя. Показан целиком,
          чтобы его можно было скопировать и сохранить: это единственный способ
          вернуться к отчёту, не запуская замер заново. */}
      <p className="auth-fine">
        Отчёт останется доступным ещё семь дней по адресу{' '}
        <a className="tech-string" href={publicLink(audit)}>
          {publicLink(audit)}
        </a>{' '}
        — сохраните его.{' '}
        <button className="lnk-btn" type="button" onClick={onAgain}>
          Проверить другой сайт
        </button>
      </p>
    </>
  );
}

/**
 * Отказы запуска, у которых есть человеческий смысл помимо общего текста.
 *
 * Ветвление по `code`, а не по `status`: так прямо сказано в контракте, и
 * причина та же, по которой мы вообще разбираем problem+json — код стабилен
 * внутри версии API, а один и тот же `429` приходит и на частые запуски, и на
 * повторный ключ идемпотентности.
 */
function AuditError({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === 'rate_limited') {
    return (
      <div className="notice warn" role="status">
        {/* Заголовок обещал сутки, а ограничений два и окна у них разные —
            причина и срок приходят с сервера строкой ниже. Заголовок,
            называющий не тот срок, спорит с текстом под собой. */}
        <b>Слишком часто</b>
        {/* Текст приходит с сервера, потому что причин у отказа две с разными
            сроками: часовое ведро частоты и СУТОЧНЫЙ потолок запусков с одного
            адреса. Один зашитый текст здесь всегда врал бы про одну из них —
            «попробуйте через несколько минут» после суточного потолка
            отправляло бы человека ждать до утра по минуте. */}
        {error.message}
      </div>
    );
  }
  return <ErrorNotice error={error} />;
}
